import { type HTMLAttributes, type ReactNode, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { Typography } from '~/_shared/design-system/ui/atoms/typography/typography';

import './empty-state.css';

interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
	title: string;
	description?: string;
	icon?: ReactNode;
	action?: ReactNode;
}

const EmptyState = ({
	title,
	description,
	icon,
	action,
	id,
	className,
	...rest
}: EmptyStateProps) => {
	// Instance-unique default id (explicit `id` prop wins) — see BaseButton.
	// The title / description element ids derive from the resolved id so
	// they stay unique alongside it.
	const auto_id = useId();
	const resolved_id = id ?? `EmptyState-${auto_id}`;
	return (
		<div
			{...rest}
			id={resolved_id}
			className={classlist('empty-state', className)}
			role='status'
		>
			{icon ? (
				<div className='empty-state__icon' aria-hidden='true'>
					{icon}
				</div>
			) : null}
			<Typography
				id={`${resolved_id}-title`}
				variant='subheading'
				as='h3'
				className='empty-state__title'
			>
				{title}
			</Typography>
			{description ? (
				<Typography
					id={`${resolved_id}-description`}
					variant='body'
					className='empty-state__description'
				>
					{description}
				</Typography>
			) : null}
			{action ? <div className='empty-state__action'>{action}</div> : null}
		</div>
	);
};

export type { EmptyStateProps };
export { EmptyState };
